// Colour zones (city.colorZones): shapes a city's admin paints on the map so a
// live order whose destination pin falls inside one shows that colour on Live.
// Nothing to do with the city's areas. The server (celer-backend
// util/color-zones.js) stores them and refuses overlapping zones; the same
// overlap check runs here while drawing, and the colouring is done here from
// the zones on cityLocation, so a repainted zone recolours the board at once.

// Admins and masters only — shops never see the colours.
export const colorZonesActive = city =>
	!!city?.withColorZonesAllowed && !!city?.withColorZones && !!city?.colorZones?.length;

const EPS = 1e-12;

// Ray casting on plain lat/lng, as on the server.
export const isInside = (point, path) => {
	let inside = false;
	for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
		const a = path[i];
		const b = path[j];
		if (
			a.lat > point.lat !== b.lat > point.lat &&
			point.lng < ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng
		) {
			inside = !inside;
		}
	}
	return inside;
};

const orient = (a, b, c) => {
	const v = (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
	return Math.abs(v) < EPS ? 0 : Math.sign(v);
};

const segmentsCross = (a, b, c, d) =>
	orient(a, b, c) * orient(a, b, d) < 0 && orient(c, d, a) * orient(c, d, b) < 0;

const onSegment = (p, a, b) =>
	orient(a, b, p) === 0 &&
	p.lat >= Math.min(a.lat, b.lat) - EPS &&
	p.lat <= Math.max(a.lat, b.lat) + EPS &&
	p.lng >= Math.min(a.lng, b.lng) - EPS &&
	p.lng <= Math.max(a.lng, b.lng) + EPS;

const onBoundary = (p, path) => path.some((a, i) => onSegment(p, a, path[(i + 1) % path.length]));

const probePoints = path => {
	const points = [...path];
	path.forEach((a, i) => {
		const b = path[(i + 1) % path.length];
		points.push({ lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 });
	});
	const center = {
		lat: path.reduce((sum, p) => sum + p.lat, 0) / path.length,
		lng: path.reduce((sum, p) => sum + p.lng, 0) / path.length,
	};
	if (isInside(center, path) && !onBoundary(center, path)) {
		points.push(center);
	}
	return points;
};

const strictlyInside = (p, path) => !onBoundary(p, path) && isInside(p, path);

// Two zones overlap when their sides cross or a point of one lies strictly
// inside the other. Sharing a border or a corner is allowed.
export const shapesOverlap = (a, b) => {
	if (a.length < 3 || b.length < 3) return false;
	for (let i = 0; i < a.length; i++) {
		for (let j = 0; j < b.length; j++) {
			if (segmentsCross(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length])) {
				return true;
			}
		}
	}
	return (
		probePoints(a).some(p => strictlyInside(p, b)) || probePoints(b).some(p => strictlyInside(p, a))
	);
};

// The order's destination, the same point the Live map pins.
const orderPoint = order => {
	const lat = order?.address?.droppedLocationPin?.latitude || order?.address?.l?.lat;
	const lng = order?.address?.droppedLocationPin?.longitude || order?.address?.l?.lng;
	return lat && lng ? { lat: Number(lat), lng: Number(lng) } : null;
};

// The colour of the zone the order's pin is in, or null. Zones never overlap,
// so the first match is the only one.
export const zoneColorForOrder = (order, city) => {
	if (!colorZonesActive(city)) return null;
	const point = orderPoint(order);
	if (!point) return null;
	const zone = city.colorZones.find(z => z?.path?.length >= 3 && isInside(point, z.path));
	return zone?.color || null;
};
